import { useState } from "react";
import { confirmPasswordReset, getAuth, verifyPasswordResetCode, applyActionCode } from "firebase/auth";
import { getAuthErrorMessage, validatePasswordLength } from "./i18n";
import { TProcessStates } from "../lib/types/general";
import { useRouteNavigation } from "../lib/common/routes";
import { useEmailVerification } from "../components/email-verifier/email-verifier";

export const useResetPasswordFirebaseLink = () => {
    const auth = getAuth();

    /**
     * Lifecycle of the status state:
     * - 'loadingFirebaseCustomPage': Initial state while verifying if the password reset link (oobCode) is valid or expired.
     * - 'firebasePageInitialized': Set by 'resetPassword' after successful link verification. This initializes the custom Firebase page so the user can enter their new password.
     * - 'working': Set by 'handleSavePassword' while the new password is being submitted and confirmed.
     * - 'done': Final state after a successful password reset.
     * - 'error': Set if the link is invalid, expired, or if the password update fails.
     */

    const [status, setStatus] = useState<TProcessStates | "loadingFirebaseCustomPage" | "firebasePageInitialized">("loadingFirebaseCustomPage");
    const [email, setEmail] = useState("");
    const [firebaseError, setFirebaseError] = useState("");
    const [errorPassword, setErrorPassword] = useState("");
    const { navigateToRoute } = useRouteNavigation();

    const resetPassword = async (oobCode: string) => {
        try {
            const email = await verifyPasswordResetCode(auth, oobCode);
            setEmail(email);
            setStatus("firebasePageInitialized");
        } catch (error) {
            setFirebaseError(getAuthErrorMessage(error));
            //Set if the link is invalid or expired.
            setStatus("error");
        }
    };

    const handleSavePassword = async (password: string, oobCode: string) => {
        const validationError = validatePasswordLength(password);

        if (validationError !== null) {
            setErrorPassword(validationError);
            return;
        }

        try {
            setStatus("working");
            await confirmPasswordReset(auth, oobCode!, password);
            setStatus("done");

            setTimeout(() => {
                navigateToRoute("home");
            }, 3000);
        } catch (error: any) {
            setFirebaseError(getAuthErrorMessage(error));
            //if the password update fails defined by firebase.
            setStatus("error");
        }
    };

    return { status, email, firebaseError, errorPassword, resetPassword, handleSavePassword };
};

export const useEmailVerificationFirebaseLink = () => {
    const auth = getAuth();

    /**
     * Lifecycle of the status state:
     * - 'firebaseVerifyingEmail': Initial state while verifying the email verification code (oobCode).
     * - 'done': Final state after successful email verification.
     * - 'error': Set if the verification fails (e.g., link expired or invalid).
     * The user only waits during this process.
     */
    const [status, setStatus] = useState<TProcessStates | "firebaseVerifyingEmail">("firebaseVerifyingEmail");
    const [error, setError] = useState("");
    const { refreshOnboarding } = useEmailVerification();

    const verifyUserEmailFB = async (oobCode: string) => {
        try {
            await applyActionCode(auth, oobCode);
            refreshOnboarding();
            setStatus("done");
        } catch (error) {
            setError(getAuthErrorMessage(error));
            setStatus("error");
        }
    };

    return { status, error, verifyUserEmailFB };
};
